import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import ReCAPTCHA from 'react-google-recaptcha'

import * as S from './styles'

const RECAPTCHA_V2_SITE_KEY = '6LekR8cUAAAAAG6yTM71N5J3jeRMhEecvwsx9sOr'

const ChevronIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M11.2848 14.3158L7.65977 10.6908C7.60977 10.6408 7.57227 10.5867 7.54727 10.5283C7.52227 10.47 7.50977 10.4075 7.50977 10.3408C7.50977 10.2075 7.5556 10.0908 7.64727 9.99082C7.73893 9.89082 7.85977 9.84082 8.00977 9.84082H15.6098C15.7598 9.84082 15.8806 9.89082 15.9723 9.99082C16.0639 10.0908 16.1098 10.2075 16.1098 10.3408C16.1098 10.3742 16.0598 10.4908 15.9598 10.6908L12.3348 14.3158C12.2514 14.3992 12.1681 14.4575 12.0848 14.4908C12.0014 14.5242 11.9098 14.5408 11.8098 14.5408C11.7098 14.5408 11.6181 14.5242 11.5348 14.4908C11.4514 14.4575 11.3681 14.3992 11.2848 14.3158Z"
      fill="#757575"
    />
  </svg>
)

const CloseIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M11.575 12.975L6.675 17.875C6.49167 18.0583 6.25833 18.15 5.975 18.15C5.69167 18.15 5.45833 18.0583 5.275 17.875C5.09167 17.6917 5 17.4583 5 17.175C5 16.8917 5.09167 16.6583 5.275 16.475L10.175 11.575L5.275 6.675C5.09167 6.49167 5 6.25833 5 5.975C5 5.69167 5.09167 5.45833 5.275 5.275C5.45833 5.09167 5.69167 5 5.975 5C6.25833 5 6.49167 5.09167 6.675 5.275L11.575 10.175L16.475 5.275C16.6583 5.09167 16.8917 5 17.175 5C17.4583 5 17.6917 5.09167 17.875 5.275C18.0583 5.45833 18.15 5.69167 18.15 5.975C18.15 6.25833 18.0583 6.49167 17.875 6.675L12.975 11.575L17.875 16.475C18.0583 16.6583 18.15 16.8917 18.15 17.175C18.15 17.4583 18.0583 17.6917 17.875 17.875C17.6917 18.0583 17.4583 18.15 17.175 18.15C16.8917 18.15 16.6583 18.0583 16.475 17.875L11.575 12.975Z"
      fill="#757575"
    />
  </svg>
)

const CustomSelect = ({ id, value, options, onChange }) => {
  const [isOpen, setIsOpen] = useState(false)
  const wrapperRef = useRef(null)

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const selectedLabel =
    options.find((opt) => String(opt.value) === String(value))?.label ||
    'Selecione...'

  return (
    <S.SelectWrapper ref={wrapperRef} id={id}>
      <S.SelectTrigger
        type="button"
        isopen={isOpen ? 'true' : 'false'}
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <span>{selectedLabel}</span>
        <ChevronIcon />
      </S.SelectTrigger>

      <S.SelectDropdown isopen={isOpen ? 'true' : 'false'}>
        {options.map((opt) => (
          <S.SelectOption
            key={opt.value}
            isselected={String(opt.value) === String(value) ? 'true' : 'false'}
            onClick={() => {
              onChange(opt.value)
              setIsOpen(false)
            }}
          >
            {opt.label}
          </S.SelectOption>
        ))}
      </S.SelectDropdown>
    </S.SelectWrapper>
  )
}

CustomSelect.defaultProps = {
  value: '',
}

CustomSelect.propTypes = {
  id: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
    })
  ).isRequired,
  onChange: PropTypes.func.isRequired,
}

const AIDialog = ({
  show,
  onClose,
  fields,
  fieldValues,
  onFieldChange,
  onGenerate,
  isLoading,
  isLoadingFields,
  textareaMaxLength,
  recaptchaVersion,
  onRecaptchaV2Change,
  recaptchaV2Ref,
}) => {
  useEffect(() => {
    if (show) {
      const scrollbarWidth =
        window.innerWidth - document.documentElement.clientWidth

      document.body.style.overflow = 'hidden'
      document.documentElement.style.overflow = 'hidden'

      if (scrollbarWidth > 0) {
        document.body.style.paddingRight = `${scrollbarWidth}px`

        const fixedEls = document.querySelectorAll('header')
        fixedEls.forEach((el) => {
          const currentPadding =
            parseInt(window.getComputedStyle(el).paddingRight, 10) || 0
          el.dataset.origPaddingRight = el.style.paddingRight
          el.style.paddingRight = `${currentPadding + scrollbarWidth}px`
        })
      }
    }
    return () => {
      document.body.style.overflow = ''
      document.documentElement.style.overflow = ''
      document.body.style.paddingRight = ''

      const fixedEls = document.querySelectorAll('header')
      fixedEls.forEach((el) => {
        el.style.paddingRight = el.dataset.origPaddingRight || ''
        delete el.dataset.origPaddingRight
      })
    }
  }, [show])

  const handleClickOverlay = ({ target, currentTarget }) => {
    if (target === currentTarget) onClose()
  }

  const textareaField = fields.find((f) => f.type === 'TEXT_AREA')
  const textareaCharCount = textareaField
    ? (fieldValues[textareaField.name] || '').length
    : 0

  return show ? (
    <S.Overlay onClick={handleClickOverlay}>
      <S.Container>
        <S.MobileHeader>
          <S.CloseButton type="button" onClick={onClose} aria-label="Fechar">
            <CloseIcon />
          </S.CloseButton>
        </S.MobileHeader>

        <S.Body>
          <S.TitleRow>
            <S.Title>Crie uma mensagem especial</S.Title>
            <S.DesktopCloseButton
              type="button"
              onClick={onClose}
              aria-label="Fechar"
            >
              <CloseIcon />
            </S.DesktopCloseButton>
          </S.TitleRow>

          <S.Subtitle>
            Com <strong>AiCasei</strong> você pode criar algo único e incrível
            ✨
          </S.Subtitle>

          <S.Divider />

          <S.FieldsContainer>
            {fields.map((field) => (
              <S.FieldGroup key={field.name}>
                <S.FieldLabel htmlFor={field.name}>{field.label}</S.FieldLabel>

                {field.type === 'SELECT' && (
                  <CustomSelect
                    id={field.name}
                    value={fieldValues[field.name]}
                    options={field.options}
                    onChange={(val) => onFieldChange(field.name, val)}
                  />
                )}

                {field.type === 'TEXT_AREA' && (
                  <S.TextAreaWrapper>
                    <S.TextArea
                      id={field.name}
                      value={fieldValues[field.name] || ''}
                      onChange={(e) =>
                        onFieldChange(field.name, e.target.value)
                      }
                      placeholder="Ex.: um apelido carinhoso, uma lembrança única, uma brincadeira interna…"
                      maxLength={field.maxLength || textareaMaxLength}
                    />
                    <S.CharCount>
                      {`${textareaCharCount}/${
                        field.maxLength || textareaMaxLength
                      }`}
                    </S.CharCount>
                  </S.TextAreaWrapper>
                )}
              </S.FieldGroup>
            ))}
          </S.FieldsContainer>

          {recaptchaVersion === 'v2' && (
            <S.RecaptchaArea>
              <ReCAPTCHA
                ref={recaptchaV2Ref}
                sitekey={RECAPTCHA_V2_SITE_KEY}
                onChange={onRecaptchaV2Change}
                onExpired={() => onRecaptchaV2Change('')}
                onErrored={() => onRecaptchaV2Change('')}
              />
            </S.RecaptchaArea>
          )}

          <S.GenerateButton
            type="button"
            isloading={isLoading ? 'true' : 'false'}
            disabled={isLoading || isLoadingFields}
            onClick={onGenerate}
          >
            {isLoading ? (
              <>
                Gerando mensagem com AiCasei...
                <S.SpinnerWrapper>
                  <S.ButtonIcon>
                    <svg
                      width="17"
                      height="17"
                      viewBox="0 0 17 17"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M5.10417 16.0104C4.09028 15.5729 3.20486 14.9757 2.44792 14.2188C1.69097 13.4618 1.09375 12.5764 0.65625 11.5625C0.21875 10.5486 0 9.46875 0 8.32292C0 7.17708 0.21875 6.10069 0.65625 5.09375C1.09375 4.08681 1.69097 3.20486 2.44792 2.44792C3.20486 1.69097 4.09028 1.09375 5.10417 0.65625C6.11806 0.21875 7.19444 0 8.33333 0C8.56944 0 8.76736 0.0798611 8.92708 0.239583C9.08681 0.399306 9.16667 0.597222 9.16667 0.833333C9.16667 1.06944 9.08681 1.26736 8.92708 1.42708C8.76736 1.58681 8.56944 1.66667 8.33333 1.66667C6.48611 1.66667 4.91319 2.31597 3.61458 3.61458C2.31597 4.91319 1.66667 6.48611 1.66667 8.33333C1.66667 10.1806 2.31597 11.7535 3.61458 13.0521C4.91319 14.3507 6.48611 15 8.33333 15C10.1806 15 11.7535 14.3507 13.0521 13.0521C14.3507 11.7535 15 10.1806 15 8.33333C15 8.09722 15.0799 7.89931 15.2396 7.73958C15.3993 7.57986 15.5972 7.5 15.8333 7.5C16.0694 7.5 16.2674 7.57986 16.4271 7.73958C16.5868 7.89931 16.6667 8.09722 16.6667 8.33333C16.6667 9.47222 16.4479 10.5486 16.0104 11.5625C15.5729 12.5764 14.9757 13.4618 14.2188 14.2188C13.4618 14.9757 12.5799 15.5729 11.5729 16.0104C10.566 16.4479 9.48958 16.6667 8.34375 16.6667C7.19792 16.6667 6.11806 16.4479 5.10417 16.0104Z"
                        fill="#9E9E9E"
                      />
                    </svg>
                  </S.ButtonIcon>
                </S.SpinnerWrapper>
              </>
            ) : (
              <>
                Criar mensagem com AiCasei
                <S.ButtonIcon>
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M15.834 7.50016L14.7923 5.2085L12.5007 4.16683L14.7923 3.12516L15.834 0.833496L16.8757 3.12516L19.1673 4.16683L16.8757 5.2085L15.834 7.50016ZM15.834 19.1668L14.7923 16.8752L12.5007 15.8335L14.7923 14.7918L15.834 12.5002L16.8757 14.7918L19.1673 15.8335L16.8757 16.8752L15.834 19.1668ZM7.50065 16.6668L5.41732 12.0835L0.833984 10.0002L5.41732 7.91683L7.50065 3.3335L9.58398 7.91683L14.1673 10.0002L9.58398 12.0835L7.50065 16.6668ZM7.50065 12.6252L8.33398 10.8335L10.1257 10.0002L8.33398 9.16683L7.50065 7.37516L6.66732 9.16683L4.87565 10.0002L6.66732 10.8335L7.50065 12.6252Z"
                      fill="white"
                    />
                  </svg>
                </S.ButtonIcon>
              </>
            )}
          </S.GenerateButton>

          <S.DividerBottom />

          <S.InfoAI>
            <S.InfoIcon>
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M7.99992 11.3333C8.18881 11.3333 8.34714 11.2694 8.47492 11.1416C8.6027 11.0138 8.66659 10.8555 8.66659 10.6666V7.99992C8.66659 7.81103 8.6027 7.6527 8.47492 7.52492C8.34714 7.39714 8.18881 7.33325 7.99992 7.33325C7.81103 7.33325 7.6527 7.39714 7.52492 7.52492C7.39714 7.6527 7.33325 7.81103 7.33325 7.99992V10.6666C7.33325 10.8555 7.39714 11.0138 7.52492 11.1416C7.6527 11.2694 7.81103 11.3333 7.99992 11.3333ZM7.99992 5.99992C8.18881 5.99992 8.34714 5.93603 8.47492 5.80825C8.6027 5.68047 8.66659 5.52214 8.66659 5.33325C8.66659 5.14436 8.6027 4.98603 8.47492 4.85825C8.34714 4.73047 8.18881 4.66659 7.99992 4.66659C7.81103 4.66659 7.6527 4.73047 7.52492 4.85825C7.39714 4.98603 7.33325 5.14436 7.33325 5.33325C7.33325 5.52214 7.39714 5.68047 7.52492 5.80825C7.6527 5.93603 7.81103 5.99992 7.99992 5.99992ZM7.99992 14.6666C7.0777 14.6666 6.21103 14.4916 5.39992 14.1416C4.58881 13.7916 3.88325 13.3166 3.28325 12.7166C2.68325 12.1166 2.20825 11.411 1.85825 10.5999C1.50825 9.78881 1.33325 8.92214 1.33325 7.99992C1.33325 7.0777 1.50825 6.21103 1.85825 5.39992C2.20825 4.58881 2.68325 3.88325 3.28325 3.28325C3.88325 2.68325 4.58881 2.20825 5.39992 1.85825C6.21103 1.50825 7.0777 1.33325 7.99992 1.33325C8.92214 1.33325 9.78881 1.50825 10.5999 1.85825C11.411 2.20825 12.1166 2.68325 12.7166 3.28325C13.3166 3.88325 13.7916 4.58881 14.1416 5.39992C14.4916 6.21103 14.6666 7.0777 14.6666 7.99992C14.6666 8.92214 14.4916 9.78881 14.1416 10.5999C13.7916 11.411 13.3166 12.1166 12.7166 12.7166C12.1166 13.3166 11.411 13.7916 10.5999 14.1416C9.78881 14.4916 8.92214 14.6666 7.99992 14.6666ZM7.99992 13.3333C9.48881 13.3333 10.7499 12.8166 11.7833 11.7833C12.8166 10.7499 13.3333 9.48881 13.3333 7.99992C13.3333 6.51103 12.8166 5.24992 11.7833 4.21659C10.7499 3.18325 9.48881 2.66659 7.99992 2.66659C6.51103 2.66659 5.24992 3.18325 4.21659 4.21659C3.18325 5.24992 2.66659 6.51103 2.66659 7.99992C2.66659 9.48881 3.18325 10.7499 4.21659 11.7833C5.24992 12.8166 6.51103 13.3333 7.99992 13.3333Z"
                  fill="#757575"
                />
              </svg>
            </S.InfoIcon>
            <S.InfoMessage>
              <strong>Importante:</strong> A AiCasei é uma inteligência
              artificial e pode cometer erros ao interpretar os dados
              fornecidos. Revise os resultados antes de compartilhá-los.
            </S.InfoMessage>
          </S.InfoAI>
        </S.Body>
      </S.Container>
    </S.Overlay>
  ) : null
}

AIDialog.defaultProps = {
  fields: [],
  fieldValues: {},
  isLoading: false,
  isLoadingFields: false,
  textareaMaxLength: 500,
  recaptchaVersion: 'v3',
  onRecaptchaV2Change: () => {},
  recaptchaV2Ref: null,
}

AIDialog.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onFieldChange: PropTypes.func.isRequired,
  onGenerate: PropTypes.func.isRequired,
  fields: PropTypes.arrayOf(PropTypes.shape({})),
  fieldValues: PropTypes.shape({}),
  isLoading: PropTypes.bool,
  isLoadingFields: PropTypes.bool,
  textareaMaxLength: PropTypes.number,
  recaptchaVersion: PropTypes.string,
  onRecaptchaV2Change: PropTypes.func,
  recaptchaV2Ref: PropTypes.shape({ current: PropTypes.any }),
}

export default AIDialog
